import { Button, Flex } from '@rocket/ui';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';

// biome-ignore lint/suspicious/noExplicitAny: list items are caller-defined
type AnyData = any;

interface PriorityListSpec {
  items: Array<AnyData>;
  selectedItems: Array<AnyData>;
  onItemsChanged: (items: Array<AnyData>) => void;
  onSelectedItemsChanged: (
    update: Array<AnyData> | ((selected: Array<AnyData>) => Array<AnyData>)
  ) => void;
}

interface PriorityListActionBarProps {
  priorityLists?: Array<PriorityListSpec>;
}

// theme.spacing(0.5, 0) === '4px 0'
const BUTTON_MARGIN = { margin: '4px 0' };

function PriorityListActionBar({ priorityLists = [] }: PriorityListActionBarProps) {
  function handleDelete() {
    priorityLists.forEach(({ items, selectedItems, onItemsChanged, onSelectedItemsChanged }) => {
      onItemsChanged(items.filter((item) => !selectedItems.includes(item)));
      // Everything selected was just deleted, so the checked set is emptied. (The
      // original filtered selectedItems against itself via a shadowed param,
      // which always produced [] — same result, spelled out here.)
      onSelectedItemsChanged([]);
    });
  }

  function moveItem(list: Array<AnyData>, item: AnyData, steps: number) {
    // Note: Function mutates list
    const pos = list.indexOf(item);
    if (pos < 0) {
      return list;
    }
    let newPos = pos + steps;
    // Stay in list bounds
    newPos = Math.min(Math.max(newPos, 0), list.length - 1);
    // Remove the item
    list.splice(pos, 1);
    // Insert it into its new position
    list.splice(newPos, 0, item);
    return list;
  }

  function handleTransfer(fromListIndex: number, toListIndex: number) {
    const fromList = priorityLists[fromListIndex];
    const toList = priorityLists[toListIndex];
    toList.onItemsChanged([...toList.items, ...fromList.selectedItems]);
    toList.onSelectedItemsChanged((selected) =>
      selected.filter((si) => !toList.selectedItems.includes(si))
    );
    toList.onSelectedItemsChanged((selected) => [...selected, ...fromList.selectedItems]);
    fromList.onItemsChanged(
      fromList.items.filter((item) => !fromList.selectedItems.includes(item))
    );
    fromList.onSelectedItemsChanged((selected) =>
      selected.filter((si) => !fromList.selectedItems.includes(si))
    );
  }

  function handleShift(steps: number) {
    priorityLists.forEach(({ items, selectedItems, onItemsChanged }) => {
      let newList = [...items];
      // Move it order so that it caterpillars up/down for multiple
      const sortDirection =
        steps < 0
          ? (a: AnyData, b: AnyData) => items.indexOf(a) - items.indexOf(b)
          : (a: AnyData, b: AnyData) => items.indexOf(b) - items.indexOf(a);
      selectedItems.sort(sortDirection).forEach((item) => {
        newList = moveItem(newList, item, steps);
      });
      onItemsChanged(newList);
    });
  }

  const nothingSelected = priorityLists.every(({ selectedItems }) => selectedItems.length === 0);

  return (
    <Flex.V alignItems="center">
      <Button
        variant="outline"
        size="sm"
        css={BUTTON_MARGIN}
        onClick={() => handleShift(-1)}
        disabled={nothingSelected}
        aria-label="move selected items up"
      >
        <LuChevronUp />
      </Button>
      {priorityLists.length > 1 && (
        <>
          <Button
            variant="outline"
            size="sm"
            css={BUTTON_MARGIN}
            onClick={() => handleTransfer(0, 1)}
            disabled={priorityLists[0].selectedItems.length === 0}
            aria-label="move selected items right"
          >
            &gt;
          </Button>
          <Button
            variant="outline"
            size="sm"
            css={BUTTON_MARGIN}
            onClick={() => handleTransfer(1, 0)}
            disabled={priorityLists[1].selectedItems.length === 0}
            aria-label="move selected items left"
          >
            &lt;
          </Button>
        </>
      )}
      <Button
        variant="outline"
        size="sm"
        css={BUTTON_MARGIN}
        onClick={() => handleShift(1)}
        disabled={nothingSelected}
        aria-label="move selected items down"
      >
        <LuChevronDown />
      </Button>
      <Button
        variant="outline"
        size="sm"
        css={BUTTON_MARGIN}
        onClick={() => handleDelete()}
        disabled={nothingSelected}
        aria-label="delete selected items"
      >
        Delete Selected
      </Button>
    </Flex.V>
  );
}

export default PriorityListActionBar;
